﻿let Capturing: boolean = false;
let Context: AudioContext | null = null;
let WorkletNode: AudioWorkletNode | null = null;
let SourceNode: MediaStreamAudioSourceNode | null = null;
let MediaStream: MediaStream | null = null;

const WORKLET_CODE = `
class PCMCaptureProcessor extends AudioWorkletProcessor {
    constructor() {
        super();
        this.buffer = [];
        this.targetSamples = 700; // Chunk size to send
        this.targetRate = 16000;  // Target sample rate
    }

    process(inputs, outputs, parameters) {
        const input = inputs[0];
        if (input && input.length > 0) {
            const channelData = input[0];
            const inputLength = channelData.length;

            // Current context sample rate (e.g., 48000 or 44100)
            const sourceRate = globalThis.sampleRate;

            // Step ratio
            const ratio = sourceRate / this.targetRate;

            // Iterate through the virtual 16kHz grid
            for (let i = 0; i < inputLength; i += ratio) {
                const index = Math.floor(i);
                const nextIndex = index + 1;
                const fraction = i - index; // Fractional part for weighting

                // Get current sample
                const s0 = channelData[index];

                // Get next sample (or current if at the end of the array)
                // *Note: for perfect audio, we should store the last sample
                // of the previous chunk, but for speech-to-text this is unnecessarily complex.
                const s1 = (nextIndex < inputLength) ? channelData[nextIndex] : s0;

                // LINEAR INTERPOLATION:
                // Calculate value between s0 and s1
                const interpolated = s0 + (s1 - s0) * fraction;

                // Clamping (just in case)
                const s = Math.max(-1, Math.min(1, interpolated));

                // Convert float -> Int16
                this.buffer.push(s < 0 ? s * 0x8000 : s * 0x7FFF);
            }

            // Send accumulated data in chunks of 700 samples (or whatever the backend needs)
            while (this.buffer.length >= this.targetSamples) {
                const chunk = this.buffer.splice(0, this.targetSamples);
                const int16Array = new Int16Array(chunk);
                this.port.postMessage(int16Array.buffer, [int16Array.buffer]);
            }
        }

        return true;
    }
}

registerProcessor('pcm-capture-processor', PCMCaptureProcessor);
`;

async function Capture(callback: (data: ArrayBuffer) => void) {

    if (Capturing) Deinitialize();
    Capturing = true;
    
    const constraints: MediaTrackConstraints = {
        echoCancellation: { ideal: true },
        noiseSuppression: { ideal: true },
        autoGainControl: { ideal: true },
        channelCount: 1,
    };

    try {
        MediaStream = await navigator.mediaDevices.getUserMedia({
            audio: constraints,
            video: false
        });

        try {
            // First, try to request target sample rate
            Context = new AudioContext({ 
                latencyHint: "interactive", 
                sampleRate: 16000 
            });
            SourceNode = Context.createMediaStreamSource(MediaStream);
        }
        catch (error) {
            try{ Context?.close(); }  
            catch { }

            // Fallback to default sample rate
            Context = new AudioContext();
            SourceNode = Context.createMediaStreamSource(MediaStream);
        }
        
        // Create blob URL for worklet
        const blob = new Blob([WORKLET_CODE], { type: 'application/javascript' });
        const workletUrl = URL.createObjectURL(blob);

        await Context.audioWorklet.addModule(workletUrl);
        URL.revokeObjectURL(workletUrl);

        WorkletNode = new AudioWorkletNode(Context, 'pcm-capture-processor');
        

        // Handle PCM data from worklet
        WorkletNode.port.onmessage = (event) => {
            callback(event.data);
        };

        // Connect audio graph
        SourceNode.connect(WorkletNode);
        WorkletNode.connect(Context.destination);

    } catch (error) {
        console.error('Failed to capture audio:', error);
        Deinitialize();
        throw error;
    }
}

function Deinitialize() {
    if (!Capturing) return;
    Capturing = false;
    
    try {
        if (WorkletNode) {
            WorkletNode.port.onmessage = null;
            WorkletNode.disconnect();
            WorkletNode = null;
        }

        if (SourceNode) {
            SourceNode.disconnect();
            SourceNode = null;
        }

        if (MediaStream) {
            MediaStream.getTracks().forEach(track => track.stop());
            MediaStream = null;
        }

        if (Context && Context.state !== 'closed') {
            Context.close().catch(x => console.error(x));
            Context = null;
        }
    } catch (error) {
        console.error('Error stopping audio capture:', error);
    }
}

export const GeminiLiveInput = {
    Capture,
    Deinitialize
};


